import { Link, useNavigate } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { BizPost } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useAuth } from "@/components/feature/AuthProvider";
import { mediaUrl } from "@/lib/media";

// 메인 직업홍보 섹션 (최신 3건)
export default function PromoSection() {
  const navigate = useNavigate();
  const { member, requireLogin } = useAuth();
  const { data, total, loading, error, reload } = useApi<BizPost[]>("bizposts.php?size=3");

  const posts = data ?? [];

  const handleWrite = () => {
    if (!requireLogin()) return;
    navigate("/promo/write");
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              직업홍보
            </h2>
            <p className="mt-2 text-foreground-600">회원 사업장의 소식과 홍보 글을 만나보세요.</p>
          </div>
          <Link
            to="/promo"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
          >
            전체 보기{total > 0 ? ` (${total}개)` : ""}
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="일시적으로 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : posts.length === 0 ? (
          <EmptyState icon="ri-megaphone-line" title="아직 등록된 직업홍보가 없습니다" />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => {
              const w = post.writer;
              const writerName =
                [w?.aho, w?.name].filter(Boolean).join(" ") ||
                [post.aho, post.name].filter(Boolean).join(" ");
              const bizName = post.biz_name || w?.biz_name || "";
              const tel = String(post.tel ?? "").trim();
              return (
                <article
                  key={post.no}
                  className="flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
                >
                  <Link to={`/promo/${post.no}`} className="group flex flex-1 flex-col">
                    <div className="relative h-40 w-full overflow-hidden bg-background-100">
                      {post.thumb ? (
                        <img
                          src={mediaUrl(post.thumb)}
                          alt={post.subject}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-secondary-100">
                          <span className="font-heading text-3xl font-bold text-secondary-800">
                            {(bizName || post.subject || "홍보").slice(0, 1)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 p-4">
                      {bizName && (
                        <p className="text-sm font-bold text-foreground-900">{bizName}</p>
                      )}
                      <h3 className="mt-1 line-clamp-1 font-heading text-base font-bold text-foreground-950">
                        {post.subject}
                      </h3>
                      {post.summary && (
                        <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">
                          {post.summary}
                        </p>
                      )}
                    </div>
                  </Link>

                  <div className="flex items-center justify-between gap-2 border-t border-background-200 px-4 py-3">
                    <div className="min-w-0">
                      {writerName && (
                        <p className="truncate text-xs font-medium text-foreground-700">
                          {writerName}
                        </p>
                      )}
                      <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-foreground-400">
                        <i className="ri-eye-line" aria-hidden="true"></i>
                        조회 {post.hit ?? 0}
                      </p>
                    </div>
                    {tel && (
                      <a
                        href={`tel:${tel.replace(/[^0-9+]/g, "")}`}
                        aria-label="전화 걸기"
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-3.5 py-1.5 text-xs font-semibold text-background-50 hover:bg-primary-600 transition-colors"
                      >
                        <i className="ri-phone-line" aria-hidden="true"></i>
                        전화
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex flex-col items-center justify-between gap-3 rounded-xl border border-dashed border-background-300 bg-background-100 p-5 sm:flex-row">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className={member ? "ri-megaphone-line text-lg" : "ri-lock-line text-lg"} aria-hidden="true"></i>
            </span>
            <p className="text-sm font-semibold text-foreground-800">
              {member ? "우리 사업장을 홍보해 보세요" : "로그인 후 작성 가능합니다"}
            </p>
          </div>
          <button
            type="button"
            onClick={handleWrite}
            className="w-full whitespace-nowrap rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-bold text-primary-950 hover:bg-secondary-600 transition-colors sm:w-auto"
          >
            <i className="ri-pencil-line mr-1.5" aria-hidden="true"></i>
            직업홍보 글쓰기
          </button>
        </div>
      </div>
    </section>
  );
}